import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import type { Contact, CreateContactRequest, ImportFromConversationsResult, Tag } from '../types';
import { Users, Plus, Edit2, Trash2, Upload, Search, X, Phone, User, AlertCircle, CheckCircle, FileSpreadsheet, Download, Tag as TagIcon } from 'lucide-react';

export const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showTagManagementModal, setShowTagManagementModal] = useState(false);
  const [showAssignTagModal, setShowAssignTagModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [contactForTagAssignment, setContactForTagAssignment] = useState<Contact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [showImportFromConversationsModal, setShowImportFromConversationsModal] = useState(false);
  const [conversationsImportResult, setConversationsImportResult] = useState<ImportFromConversationsResult | null>(null);
  const [conversationsImportError, setConversationsImportError] = useState<string | null>(null);
  const [isImportingFromConversations, setIsImportingFromConversations] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagModalError, setTagModalError] = useState<string | null>(null);
  const [tagModalSuccess, setTagModalSuccess] = useState<string | null>(null);
  const [isSavingTag, setIsSavingTag] = useState(false);
  const [isAssigningTags, setIsAssigningTags] = useState(false);
  const [formData, setFormData] = useState<CreateContactRequest>({
    name: '',
    phone: '',
  });
  const [tagFormData, setTagFormData] = useState({
    name: '',
    color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0'),
  });
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [showBulkDeleteResultModal, setShowBulkDeleteResultModal] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState<{ success: boolean; message: string; count?: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        page: currentPage.toString(),
        limit: '20',
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      // First request - might return from cache
      const response = await api.getContacts(params);
      setContacts(response.data.contacts);
      setTotalPages(response.data.pagination.totalPages);
      setTotal(response.data.pagination.total);
      setIsLoading(false);

      // If data came from cache, fetch fresh data in background
      if (response.fromCache) {
        console.log('📦 Data from cache, fetching fresh data...');

        // Fetch fresh data in background (skip cache)
        const freshResponse = await api.getContacts(params, true);

        // Compare data - check if anything changed
        const hasChanges =
          JSON.stringify(response.data.contacts) !== JSON.stringify(freshResponse.data.contacts) ||
          response.data.pagination.total !== freshResponse.data.pagination.total;

        if (hasChanges) {
          console.log('🔄 Fresh data differs from cache, updating...');
          setContacts(freshResponse.data.contacts);
          setTotalPages(freshResponse.data.pagination.totalPages);
          setTotal(freshResponse.data.pagination.total);

          // Show notification that data was updated
          setShowUpdateNotification(true);
          setTimeout(() => setShowUpdateNotification(false), 3000);
        } else {
          console.log('✅ Cache was up to date');
        }
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const fetchedTags = await api.getTags();
      setTags(fetchedTags);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadContacts();
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage]);

  const handleOpenModal = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        phone: contact.phone,
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        phone: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await api.updateContact(editingContact.id, formData);
      } else {
        await api.createContact(formData);
      }
      handleCloseModal();
      loadContacts();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao salvar contato');
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;

    try {
      await api.deleteContact(contactToDelete.id);
      setShowDeleteModal(false);
      setContactToDelete(null);
      loadContacts();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao excluir contato');
    }
  };

  const handleSelectAll = () => {
    if (selectedContactIds.size === contacts.length) {
      // Desselecionar todos
      setSelectedContactIds(new Set());
    } else {
      // Selecionar todos
      setSelectedContactIds(new Set(contacts.map(c => c.id)));
    }
  };

  const handleSelectContact = (contactId: string) => {
    const newSelected = new Set(selectedContactIds);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContactIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedContactIds.size === 0) return;

    const countToDelete = selectedContactIds.size;

    try {
      const result = await api.deleteMultipleContacts(Array.from(selectedContactIds));
      setShowBulkDeleteModal(false);
      setSelectedContactIds(new Set());

      // Show success modal
      setBulkDeleteResult({
        success: true,
        message: `${result.deleted} ${result.deleted === 1 ? 'contato excluído' : 'contatos excluídos'} com sucesso!`,
        count: result.deleted
      });
      setShowBulkDeleteResultModal(true);

      loadContacts();
    } catch (error: any) {
      setShowBulkDeleteModal(false);

      // Show error modal
      let errorMessage = 'Erro ao excluir contatos';

      if (!error.response) {
        errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
      } else if (error.response.status === 502) {
        errorMessage = 'Servidor temporariamente indisponível (502 Bad Gateway).';
      } else if (error.response.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else {
        errorMessage = error.response?.data?.error || 'Erro ao excluir contatos';
      }

      setBulkDeleteResult({
        success: false,
        message: errorMessage,
        count: countToDelete
      });
      setShowBulkDeleteResultModal(true);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setImportResult(null);
      setImportError(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    setImportError(null);
    setImportResult(null);

    try {
      const result = await api.importContacts(selectedFile);

      // Import was successful - set the result
      setImportResult(result);
      setSelectedFile(null);

      // Wait a bit for database to fully commit, then reload
      await new Promise(resolve => setTimeout(resolve, 500));

      // Try to reload contacts, but don't fail if this errors
      try {
        // Reset to page 1 to see all contacts
        setCurrentPage(1);
        await loadContacts();
      } catch (reloadError) {
        console.error('Failed to reload contacts after import, but import was successful:', reloadError);
        // Don't show error to user since import itself succeeded
      }
    } catch (error: any) {
      // Only show error if the import itself failed
      let errorMessage = 'Erro ao importar contatos.';

      // Check for specific error types
      if (error.response?.status === 504) {
        // Gateway Timeout - file is too large or processing took too long
        errorMessage = 'Tempo limite excedido. O arquivo pode ser muito grande ou o processamento está demorando demais. Tente com um arquivo menor ou entre em contato com o suporte.';
      } else if (error.response?.status === 413) {
        // Payload Too Large
        errorMessage = 'Arquivo muito grande. O tamanho máximo permitido é 20MB. Tente com um arquivo menor.';
      } else if (error.response?.data) {
        // Server returned error details
        const data = error.response.data;
        if (data.error && data.details) {
          errorMessage = `${data.error}: ${data.details}`;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.details) {
          errorMessage = data.details;
        }
      } else if (error.request && !error.response) {
        // Request was made but no response received (network error)
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else {
        // Generic fallback - likely a format or validation error
        errorMessage = 'Erro ao importar contatos. Verifique o formato do arquivo e tente novamente.';
      }

      console.error('Import error:', error);
      setImportError(errorMessage);
      setImportResult(null);
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFromConversations = async () => {
    try {
      setIsImportingFromConversations(true);
      setConversationsImportResult(null);
      setConversationsImportError(null);
      const result = await api.importContactsFromConversations();
      setConversationsImportResult(result);
      loadContacts();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Erro ao importar contatos dos atendimentos. Tente novamente.';
      setConversationsImportError(errorMessage);
      setConversationsImportResult(null);
    } finally {
      setIsImportingFromConversations(false);
    }
  };

  const handleSaveTag = async () => {
    if (!tagFormData.name.trim()) {
      setTagModalError('O nome da etiqueta é obrigatório');
      return;
    }

    setIsSavingTag(true);
    setTagModalError(null);
    setTagModalSuccess(null);

    try {
      if (editingTag) {
        await api.updateTag(editingTag.id, tagFormData);
        setTagModalSuccess('Etiqueta atualizada com sucesso!');
      } else {
        await api.createTag(tagFormData);
        setTagModalSuccess('Etiqueta criada com sucesso!');
      }

      setTagFormData({ name: '', color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0') });
      setEditingTag(null);
      await loadTags();

      // Clear success message after 3 seconds
      setTimeout(() => setTagModalSuccess(null), 3000);
    } catch (error: any) {
      setTagModalError(error.response?.data?.error || 'Erro ao salvar etiqueta');
    } finally {
      setIsSavingTag(false);
    }
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (!confirm(`Tem certeza que deseja excluir a etiqueta "${tag.name}"?`)) {
      return;
    }

    try {
      await api.deleteTag(tag.id);
      setTagModalSuccess('Etiqueta excluída com sucesso!');
      await loadTags();

      // Clear success message after 3 seconds
      setTimeout(() => setTagModalSuccess(null), 3000);
    } catch (error: any) {
      setTagModalError(error.response?.data?.error || 'Erro ao excluir etiqueta');
    }
  };

  const formatPhone = (phone: string) => {
    if (phone.length === 13 && phone.startsWith('55')) {
      const areaCode = phone.substring(2, 4);
      const firstPart = phone.substring(4, 9);
      const secondPart = phone.substring(9, 13);
      return `+55 (${areaCode}) ${firstPart}-${secondPart}`;
    }
    return phone;
  };

  if (isLoading && contacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Update Notification */}
      {showUpdateNotification && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <CheckCircle className="h-5 w-5" />
          <span>Dados atualizados!</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Users className="w-8 h-8" />
            Contatos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gerencie seus contatos de atendimento ({total} {total === 1 ? 'contato' : 'contatos'})
            {selectedContactIds.size > 0 && (
              <span className="ml-2 text-blue-600 dark:text-blue-400 font-medium">
                {selectedContactIds.size} selecionado{selectedContactIds.size !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          {selectedContactIds.size > 0 && (
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              <Trash2 className="w-5 h-5" />
              Excluir Selecionados ({selectedContactIds.size})
            </button>
          )}
          <button
            onClick={() => {
              setShowTagManagementModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <TagIcon className="w-5 h-5" />
            Etiquetas
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <Upload className="w-5 h-5" />
            Importar Planilha
          </button>
          <button
            onClick={() => setShowImportFromConversationsModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            <Download className="w-5 h-5" />
            Importar dos Atendimentos
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            Novo Contato
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou telefone..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Contacts List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left w-12">
                <input
                  type="checkbox"
                  checked={contacts.length > 0 && selectedContactIds.size === contacts.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Telefone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Etiquetas
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Criado em
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {contacts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato cadastrado'}
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedContactIds.has(contact.id)}
                      onChange={() => handleSelectContact(contact.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{contact.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900 dark:text-white">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {formatPhone(contact.phone)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {contact.tags && contact.tags.length > 0 ? (
                        contact.tags.map((ct) => (
                          <span
                            key={ct.id}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: ct.tag.color }}
                          >
                            {ct.tag.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 text-xs italic">Sem etiquetas</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(contact.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        // Set contact and pre-select its tags
                        const contactTags = contact.tags?.map(ct => ct.tag.id) || [];
                        setContactForTagAssignment(contact);
                        setSelectedTagIds(contactTags);
                        setShowAssignTagModal(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 mr-4"
                      title="Atribuir Etiquetas"
                    >
                      <TagIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleOpenModal(contact)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setContactToDelete(contact);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Página {currentPage} de {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                {editingContact ? 'Editar Contato' : 'Novo Contato'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Nome do contato"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefone *
                </label>
                <input
                  type="text"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="(11) 99999-9999 ou 5511999999999"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Formatos aceitos: (11) 99999-9999, 11999999999, 5511999999999, +5511999999999
                </p>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingContact ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && contactToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 dark:text-white">Confirmar Exclusão</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Tem certeza que deseja excluir o contato <strong>{contactToDelete.name}</strong>?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setContactToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 dark:text-white">Confirmar Exclusão em Massa</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Tem certeza que deseja excluir <strong>{selectedContactIds.size}</strong> {selectedContactIds.size === 1 ? 'contato' : 'contatos'} selecionado{selectedContactIds.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-6">
              ⚠️ Esta ação não pode ser desfeita!
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir Todos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Result Modal */}
      {showBulkDeleteResultModal && bulkDeleteResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              {bulkDeleteResult.success ? (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              ) : (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-4">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              )}
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {bulkDeleteResult.success ? 'Sucesso' : 'Erro'}
              </h2>
            </div>
            <p className={`mb-6 ${bulkDeleteResult.success ? 'text-gray-600 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
              {bulkDeleteResult.message}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowBulkDeleteResultModal(false);
                  setBulkDeleteResult(null);
                }}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold dark:text-white">Importar Contatos</h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setSelectedFile(null);
                  setImportResult(null);
                  setImportError(null);
                  setShowAllErrors(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Instruções:</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>A planilha deve ter as seguintes colunas com os cabeçalhos:
                    <ul className="ml-6 mt-1 space-y-1">
                      <li><strong>Coluna 1:</strong> "Nome" ou "Sócio" (obrigatório)</li>
                      <li><strong>Coluna 2:</strong> "Telefone" ou "Telefone(s)" (obrigatório)</li>
                      <li><strong>Coluna 3:</strong> "Etiqueta" (obrigatório - se deixar em branco, será atribuída "Sem Etiqueta")</li>
                    </ul>
                  </li>
                  <li>Se houver múltiplos telefones separados por <strong>/</strong>, apenas o primeiro será importado</li>
                  <li>Formatos de telefone aceitos: (11) 99999-9999, 11999999999, 5511999999999, +5511999999999</li>
                  <li>Contatos com etiqueta em branco receberão automaticamente a etiqueta "Sem Etiqueta"</li>
                  <li>Contatos duplicados terão suas etiquetas atualizadas de acordo com a planilha</li>
                  <li>Formatos de arquivo aceitos: .xlsx, .xls, .xlsm, .ods, .xltx</li>
                </ul>
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.ods,.xltx"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {!selectedFile ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors bg-gray-50 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-700 dark:text-gray-300">Clique para escolher um arquivo</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Excel, LibreOffice ou outros formatos de planilha
                        </p>
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0">
                          <FileSpreadsheet className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setImportResult(null);
                          setImportError(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="ml-3 p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                        title="Remover arquivo"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      Escolher outro arquivo
                    </button>
                  </div>
                )}
              </div>

              {/* Error Display */}
              {importError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-red-900 dark:text-red-300 mb-1">Erro na Importação</p>
                      <p className="text-sm text-red-800 dark:text-red-400">{importError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isImporting && !importResult && !importError && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400"></div>
                    <div className="text-center">
                      <p className="font-semibold text-blue-900 dark:text-blue-300 mb-1">Processando planilha...</p>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Isso pode levar alguns segundos dependendo do tamanho do arquivo
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {importResult && (
                <div className="space-y-3">
                  {/* Success Summary */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-green-900 dark:text-green-300 mb-3">Importação concluída!</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Total de linhas</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">{importResult.results.total}</p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Importados (novos)</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">{importResult.results.imported}</p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Atualizados</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">{importResult.results.updated}</p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Etiquetas criadas</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">{importResult.results.tagsCreated || 0}</p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Ignorados</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">{importResult.results.skipped}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Empty Rows Info */}
                  {importResult.results.emptyRows > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-gray-700 dark:text-gray-300">
                            {importResult.results.emptyRows} {importResult.results.emptyRows === 1 ? 'linha vazia foi ignorada' : 'linhas vazias foram ignoradas'}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                            Linhas sem nome e telefone são automaticamente desconsideradas
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Errors */}
                  {importResult.results.errors.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm flex-1">
                          <p className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
                            Erros encontrados ({importResult.results.errors.length}):
                          </p>
                          <div className="max-h-60 overflow-y-auto bg-white dark:bg-yellow-950/30 rounded p-2">
                            <ul className="text-yellow-800 dark:text-yellow-400 space-y-1.5">
                              {(showAllErrors
                                ? importResult.results.errors
                                : importResult.results.errors.slice(0, 15)
                              ).map((error: any, idx: number) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="text-yellow-600 dark:text-yellow-500 font-mono text-xs flex-shrink-0">
                                    L{error.row}:
                                  </span>
                                  <span className="flex-1">
                                    {error.reason}
                                    {error.phone && <span className="text-xs ml-1">({error.phone})</span>}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          {importResult.results.errors.length > 15 && (
                            <button
                              onClick={() => setShowAllErrors(!showAllErrors)}
                              className="mt-2 text-yellow-700 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 text-sm font-medium underline"
                            >
                              {showAllErrors
                                ? 'Mostrar menos erros'
                                : `Mostrar todos os ${importResult.results.errors.length} erros`}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSelectedFile(null);
                    setImportResult(null);
                    setImportError(null);
                    setShowAllErrors(false);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  Fechar
                </button>
                <button
                  onClick={handleImport}
                  disabled={!selectedFile || isImporting}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Importar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import from Conversations Modal */}
      {showImportFromConversationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold dark:text-white">Importar Contatos dos Atendimentos</h2>
              <button
                onClick={() => {
                  setShowImportFromConversationsModal(false);
                  setConversationsImportResult(null);
                  setConversationsImportError(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Como funciona:</h3>
                <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>O sistema vai buscar todos os telefones das conversas de atendimento</li>
                  <li>Apenas contatos novos (que ainda não existem) serão importados</li>
                  <li>Contatos duplicados serão automaticamente ignorados</li>
                  <li>O nome do contato será obtido da conversa</li>
                </ul>
              </div>

              {/* Error Display for Conversations Import */}
              {conversationsImportError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-red-900 dark:text-red-300 mb-1">Erro na Importação</p>
                      <p className="text-sm text-red-800 dark:text-red-400">{conversationsImportError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State for Conversations */}
              {isImportingFromConversations && !conversationsImportResult && !conversationsImportError && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 dark:border-purple-800 border-t-purple-600 dark:border-t-purple-400"></div>
                    <div className="text-center">
                      <p className="font-semibold text-purple-900 dark:text-purple-300 mb-1">Buscando contatos dos atendimentos...</p>
                      <p className="text-sm text-purple-700 dark:text-purple-400">
                        Aguarde enquanto processamos as conversas
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!conversationsImportResult && !conversationsImportError && !isImportingFromConversations && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mb-4">
                    <Download className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 text-center">
                    Pronto para importar contatos dos seus atendimentos?
                  </p>
                  <button
                    onClick={handleImportFromConversations}
                    disabled={isImportingFromConversations}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isImportingFromConversations ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Importando...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5" />
                        Iniciar Importação
                      </>
                    )}
                  </button>
                </div>
              )}

              {conversationsImportResult && (
                <div className="space-y-3">
                  {/* Success Summary */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-green-900 dark:text-green-300 mb-3">Importação concluída!</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Total encontrado</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">
                              {conversationsImportResult.results.total}
                            </p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Importados (novos)</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">
                              {conversationsImportResult.results.imported}
                            </p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Já existentes</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">
                              {conversationsImportResult.results.alreadyExists}
                            </p>
                          </div>
                          <div className="bg-white dark:bg-green-950/30 rounded p-2">
                            <p className="text-gray-500 dark:text-gray-400 text-xs">Com erro</p>
                            <p className="text-green-900 dark:text-green-300 font-semibold text-lg">
                              {conversationsImportResult.results.errors.length}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Errors */}
                  {conversationsImportResult.results.errors.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm flex-1">
                          <p className="font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
                            Erros encontrados ({conversationsImportResult.results.errors.length}):
                          </p>
                          <div className="max-h-40 overflow-y-auto bg-white dark:bg-yellow-950/30 rounded p-2">
                            <ul className="text-yellow-800 dark:text-yellow-400 space-y-1.5">
                              {conversationsImportResult.results.errors.slice(0, 10).map((error: any, idx: number) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <span className="flex-1">
                                    {error.name && <strong>{error.name}:</strong>} {error.reason}
                                    {error.phone && <span className="text-xs ml-1">({error.phone})</span>}
                                  </span>
                                </li>
                              ))}
                              {conversationsImportResult.results.errors.length > 10 && (
                                <li className="text-yellow-700 dark:text-yellow-500 italic">
                                  ... e mais {conversationsImportResult.results.errors.length - 10} erros
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowImportFromConversationsModal(false);
                    setConversationsImportResult(null);
                    setConversationsImportError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Tag Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                {editingTag ? 'Editar Etiqueta' : 'Nova Etiqueta'}
              </h2>
              <button onClick={() => setShowTagModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Success Message */}
            {tagModalSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-800 dark:text-green-200">{tagModalSuccess}</span>
              </div>
            )}

            {/* Error Message */}
            {tagModalError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                <span className="text-sm text-red-800 dark:text-red-200">{tagModalError}</span>
              </div>
            )}

            <form onSubmit={async (e) => {
              e.preventDefault();
              setIsSavingTag(true);
              setTagModalError(null);
              setTagModalSuccess(null);

              try {
                if (editingTag) {
                  await api.updateTag(editingTag.id, tagFormData);
                  setTagModalSuccess('Etiqueta atualizada com sucesso!');
                } else {
                  await api.createTag(tagFormData);
                  setTagModalSuccess('Etiqueta criada com sucesso!');
                }

                // Reload tags
                await loadTags();

                // Close modal after 1.5 seconds
                setTimeout(() => {
                  setShowTagModal(false);
                  setTagModalSuccess(null);
                  setTagModalError(null);
                }, 1500);
              } catch (error: any) {
                console.error('Error saving tag:', error);
                const errorMsg = error.response?.data?.error || error.response?.data?.details || 'Erro ao salvar etiqueta';
                setTagModalError(errorMsg);
              } finally {
                setIsSavingTag(false);
              }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={tagFormData.name}
                  onChange={(e) => setTagFormData({ ...tagFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Nome da etiqueta"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cor *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    required
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({ ...tagFormData, color: e.target.value })}
                    className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    required
                    value={tagFormData.color}
                    onChange={(e) => setTagFormData({ ...tagFormData, color: e.target.value })}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono"
                    placeholder="#FF5733"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Formato hexadecimal (ex: #FF5733)
                </p>
              </div>

              <div className="flex gap-3 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowTagModal(false);
                    setTagModalError(null);
                    setTagModalSuccess(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSavingTag}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSavingTag && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>{isSavingTag ? 'Salvando...' : (editingTag ? 'Salvar' : 'Criar')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Tags Modal */}
      {showAssignTagModal && contactForTagAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold dark:text-white">
                Atribuir Etiquetas
              </h2>
              <button onClick={() => setShowAssignTagModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Contato: <strong className="text-gray-900 dark:text-white">{contactForTagAssignment.name}</strong>
              </p>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
              {tags.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">
                  Nenhuma etiqueta cadastrada. Crie uma etiqueta primeiro.
                </p>
              ) : (
                tags.map((tag) => (
                  <label
                    key={tag.id}
                    className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTagIds([...selectedTagIds, tag.id]);
                        } else {
                          setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                        }
                      }}
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                    <span
                      className="ml-3 px-3 py-1 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowAssignTagModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setIsAssigningTags(true);
                  try {
                    await api.assignTagsToContact(contactForTagAssignment.id, selectedTagIds);
                    setShowAssignTagModal(false);
                    loadContacts();
                  } catch (error: any) {
                    alert(error.response?.data?.error || 'Erro ao atribuir etiquetas');
                  } finally {
                    setIsAssigningTags(false);
                  }
                }}
                disabled={isAssigningTags}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAssigningTags && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{isAssigningTags ? 'Salvando...' : 'Salvar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tags Management Modal */}
      {showTagManagementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold dark:text-white">Gerenciar Etiquetas</h2>
              <button
                onClick={() => {
                  setShowTagManagementModal(false);
                  setEditingTag(null);
                  setTagModalError(null);
                  setTagModalSuccess(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Create New Tag Form */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {editingTag ? 'Editar Etiqueta' : 'Nova Etiqueta'}
              </h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={tagFormData.name}
                  onChange={(e) => setTagFormData({ ...tagFormData, name: e.target.value })}
                  placeholder="Nome da etiqueta"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                <input
                  type="color"
                  value={tagFormData.color}
                  onChange={(e) => setTagFormData({ ...tagFormData, color: e.target.value })}
                  className="w-16 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
                />
                {editingTag ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveTag}
                      disabled={isSavingTag || !tagFormData.name.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingTag ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingTag(null);
                        setTagFormData({ name: '', color: '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0') });
                      }}
                      className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleSaveTag}
                    disabled={isSavingTag || !tagFormData.name.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSavingTag ? 'Criando...' : 'Criar'}
                  </button>
                )}
              </div>

              {tagModalError && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-400">{tagModalError}</p>
                </div>
              )}

              {tagModalSuccess && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-800 dark:text-green-400">{tagModalSuccess}</p>
                </div>
              )}
            </div>

            {/* Tags List */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Etiquetas Existentes ({tags.length})
              </h3>
              {tags.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <TagIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma etiqueta criada ainda</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {tags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-md transition"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-500"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="font-medium dark:text-white">{tag.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingTag(tag);
                            setTagFormData({ name: tag.name, color: tag.color });
                            setTagModalError(null);
                            setTagModalSuccess(null);
                          }}
                          className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTag(tag)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowTagManagementModal(false);
                  setEditingTag(null);
                  setTagModalError(null);
                  setTagModalSuccess(null);
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
